<script setup>
import { computed, h, ref, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import SuggestedScenarios from 'dashboard/components-next/captain/assistant/SuggestedRules.vue';
import ScenariosCard from 'dashboard/components-next/captain/assistant/ScenariosCard.vue';
import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import AddNewScenariosDialog from 'dashboard/components-next/captain/assistant/AddNewScenariosDialog.vue';
import SyncPresetDialog from 'dashboard/components-next/captain/pageComponents/assistant/SyncPresetDialog.vue';
import LinkPresetDialog from 'dashboard/components-next/captain/pageComponents/assistant/LinkPresetDialog.vue';
import GenerateScenariosDialog from 'dashboard/components-next/captain/pageComponents/assistant/GenerateScenariosDialog.vue';
import PendingScenariosSection from 'dashboard/components-next/captain/assistant/PendingScenariosSection.vue';
import ScenarioAnalysisStatus from 'dashboard/components-next/captain/assistant/ScenarioAnalysisStatus.vue';
import CaptainScenarios from 'dashboard/api/captain/scenarios';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { formatMessage } = useMessageFormatter();
const assistantId = computed(() => Number(route.params.assistantId));

const uiFlags = useMapGetter('captainScenarios/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const scenarios = useMapGetter('captainScenarios/getRecords');

const searchQuery = ref('');

// Only assistants created from a business preset can be synced back to it.
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);
const businessPresetId = computed(
  () => assistant.value?.config?.business_preset_id
);

const syncPresetDialogRef = ref(null);
const openSyncPresetDialog = () =>
  nextTick(() => syncPresetDialogRef.value.dialogRef.open());

const linkPresetDialogRef = ref(null);
const openLinkPresetDialog = () =>
  nextTick(() => linkPresetDialogRef.value.dialogRef.open());

const refreshScenarios = () =>
  store.dispatch('captainScenarios/get', { assistantId: assistantId.value });

// The sync that just ran also wrote business_preset_id onto the assistant, so re-fetching it
// is what flips this page from "Vincular a un rubro" over to "Actualizar desde el rubro".
const handleLinked = () => {
  store.dispatch('captainAssistants/get');
  refreshScenarios();
};

const getToolsFromInstruction = instruction => [
  ...new Set(
    [...(instruction?.matchAll(/\(tool:\/\/([^)]+)\)/g) ?? [])].map(m => m[1])
  ),
];

// Proposals are kept out of the Vuex store on purpose: it holds one flat list of records,
// so loading them there would show them among the assistant's real scenarios.
const pendingScenarios = ref([]);
const generateDialogRef = ref(null);
const generationStatus = ref('');
const proposedCount = ref(null);
let pollTimer = null;

const fetchPendingScenarios = async () => {
  try {
    const { data } = await CaptainScenarios.getPending({
      assistantId: assistantId.value,
    });
    pendingScenarios.value = data.payload ?? [];
  } catch (error) {
    pendingScenarios.value = [];
  }
};

// The analysis takes minutes, so its state lives on the assistant rather than in this
// component: coming back to the page later still shows what happened.
const fetchGenerationStatus = async () => {
  try {
    const { data } = await CaptainScenarios.generationPreview({
      assistantId: assistantId.value,
    });
    generationStatus.value = data.status ?? '';
    proposedCount.value = data.proposed_count ?? null;
  } catch (error) {
    generationStatus.value = '';
  }
};

const openGenerateDialog = () => nextTick(() => generateDialogRef.value.open());

const stopPolling = () => {
  if (!pollTimer) return;
  clearInterval(pollTimer);
  pollTimer = null;
};

const pollWhileAnalysing = () => {
  if (pollTimer) return;

  let attempts = 0;
  pollTimer = setInterval(async () => {
    attempts += 1;
    await Promise.all([fetchGenerationStatus(), fetchPendingScenarios()]);
    // Ten minutes is well past a normal run; stop rather than poll a stuck job forever.
    if (generationStatus.value !== 'running' || attempts >= 40) stopPolling();
  }, 15000);
};

const startAnalysis = () => {
  generationStatus.value = 'running';
  proposedCount.value = null;
  pollWhileAnalysing();
};

const isCancellingAnalysis = ref(false);

// The shot is given back on the server, so the dialog can be opened again straight away.
const cancelAnalysis = async () => {
  isCancellingAnalysis.value = true;
  try {
    await CaptainScenarios.cancelGeneration({ assistantId: assistantId.value });
    stopPolling();
    generationStatus.value = 'cancelled';
    proposedCount.value = null;
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.ANALYSIS.CANCELLED'));
  } catch (error) {
    // Most likely it finished in the meantime: show whatever state it ended in.
    await Promise.all([fetchGenerationStatus(), fetchPendingScenarios()]);
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.ANALYSIS.CANCEL_ERROR'));
  } finally {
    isCancellingAnalysis.value = false;
  }
};

const approveScenario = async id => {
  try {
    await CaptainScenarios.approve({ assistantId: assistantId.value, id });
    pendingScenarios.value = pendingScenarios.value.filter(s => s.id !== id);
    await refreshScenarios();
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.PENDING.APPROVED'));
  } catch (error) {
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.ERROR'));
  }
};

const discardScenario = async id => {
  try {
    await CaptainScenarios.delete({ assistantId: assistantId.value, id });
    pendingScenarios.value = pendingScenarios.value.filter(s => s.id !== id);
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.ERROR'));
  }
};

const updatePendingScenario = async scenario => {
  await CaptainScenarios.update(
    { assistantId: assistantId.value, id: scenario.id },
    { ...scenario, tools: getToolsFromInstruction(scenario.instruction) }
  );
  await fetchPendingScenarios();
};

const LINK_INSTRUCTION_CLASS =
  '[&_a[href^="tool://"]]:text-n-iris-11 [&_a:not([href^="tool://"])]:text-n-slate-12 [&_a]:pointer-events-none [&_a]:cursor-default';

const renderInstruction = instruction => () =>
  h('span', {
    class: `text-sm text-n-slate-12 py-4 prose prose-sm min-w-0 break-words ${LINK_INSTRUCTION_CLASS}`,
    innerHTML: instruction,
  });

// Suggested example scenarios for quick add
const scenariosExample = [
  {
    id: 1,
    title: 'Prospective Buyer',
    description:
      'Handle customers who are showing interest in purchasing a license',
    instruction:
      'If someone is interested in purchasing a license, ask them for following:\n\n1. How many licenses are they willing to purchase?\n2. Are they migrating from another platform?\n. Once these details are collected, do the following steps\n1. add a private note to with the information you collected using [Add Private Note](tool://add_private_note)\n2. Add label "sales" to the contact using [Add Label to Conversation](tool://add_label_to_conversation)\n3. Reply saying "one of us will reach out soon" and provide an estimated timeline for the response and [Handoff to Human](tool://handoff)',
    tools: ['add_private_note', 'add_label_to_conversation', 'handoff'],
  },
];

const filteredScenarios = computed(() => {
  const query = searchQuery.value.trim();
  const source = scenarios.value;
  if (!query) return source;
  return picoSearch(source, query, ['title', 'description', 'instruction']);
});

const shouldShowSuggestedRules = computed(() => {
  return uiSettings.value?.show_scenarios_suggestions !== false;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_scenarios_suggestions: false });
};

// Bulk selection & hover state
const bulkSelectedIds = ref(new Set());
const hoveredCard = ref(null);

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const buildSelectedCountLabel = computed(() => {
  const count = scenarios.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleRuleHover = (isHovered, id) => {
  hoveredCard.value = isHovered ? id : null;
};

const updateScenario = async scenario => {
  try {
    await store.dispatch('captainScenarios/update', {
      id: scenario.id,
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

const deleteScenario = async id => {
  try {
    await store.dispatch('captainScenarios/delete', {
      id,
      assistantId: assistantId.value,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

// TODO: Add bulk delete endpoint
const bulkDeleteScenarios = async ids => {
  const idsArray = ids || Array.from(bulkSelectedIds.value);
  await Promise.all(
    idsArray.map(id =>
      store.dispatch('captainScenarios/delete', {
        id,
        assistantId: assistantId.value,
      })
    )
  );
  bulkSelectedIds.value = new Set();
  useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
};

const addScenario = async scenario => {
  try {
    await store.dispatch('captainScenarios/create', {
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

const addAllExampleScenarios = async () => {
  try {
    scenariosExample.forEach(async scenario => {
      await store.dispatch('captainScenarios/create', {
        assistantId: assistantId.value,
        ...scenario,
      });
    });
    useAlert(t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAPTAIN.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  store.dispatch('captainScenarios/get', {
    assistantId: assistantId.value,
  });
  store.dispatch('captainTools/getTools');
  // Needed to know whether this assistant came from a business preset. Reaching this page
  // directly does not otherwise load the assistants.
  store.dispatch('captainAssistants/get');
  // The inbox filter of the analysis dialog needs them.
  store.dispatch('inboxes/get');
  fetchPendingScenarios();
  // An analysis launched before a reload, or from another tab, must still show up here.
  fetchGenerationStatus().then(() => {
    if (generationStatus.value === 'running') pollWhileAnalysing();
  });
});

onUnmounted(stopPolling);
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.DOCUMENTS.HEADER')"
    :is-fetching="isFetching"
    :show-know-more="false"
    :show-pagination-footer="false"
  >
    <template #body>
      <div class="flex items-start justify-between gap-4">
        <SettingsHeader
          :heading="$t('CAPTAIN.ASSISTANTS.SCENARIOS.TITLE')"
          :description="$t('CAPTAIN.ASSISTANTS.SCENARIOS.DESCRIPTION')"
        />
        <div class="flex gap-2 flex-shrink-0">
          <Button
            :label="$t('CAPTAIN.ASSISTANTS.SCENARIOS.GENERATE.BUTTON')"
            icon="i-lucide-sparkles"
            faded
            slate
            sm
            @click="openGenerateDialog"
          />
          <Button
            v-if="businessPresetId"
            :label="$t('CAPTAIN.ASSISTANTS.SCENARIOS.SYNC_PRESET.BUTTON')"
            icon="i-lucide-refresh-ccw"
            faded
            slate
            sm
            @click="openSyncPresetDialog"
          />
          <Button
            v-else
            :label="$t('CAPTAIN.ASSISTANTS.SCENARIOS.LINK_PRESET.BUTTON')"
            icon="i-lucide-link"
            faded
            slate
            sm
            @click="openLinkPresetDialog"
          />
        </div>
      </div>

      <ScenarioAnalysisStatus
        :status="generationStatus"
        :proposed-count="proposedCount"
        :is-cancelling="isCancellingAnalysis"
        @cancel="cancelAnalysis"
      />

      <PendingScenariosSection
        v-if="pendingScenarios.length"
        :scenarios="pendingScenarios"
        @approve="approveScenario"
        @discard="discardScenario"
        @update="updatePendingScenario"
      />
      <div v-if="shouldShowSuggestedRules" class="flex mt-7 flex-col gap-4">
        <SuggestedScenarios
          :title="$t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TITLE')"
          :items="scenariosExample"
          @close="closeSuggestedRules"
          @add="addAllExampleScenarios"
        >
          <template #default="{ item }">
            <div class="flex items-center gap-3 justify-between">
              <span class="text-sm text-n-slate-12">
                {{ item.title }}
              </span>
              <Button
                :label="
                  $t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.ADD_SINGLE')
                "
                ghost
                xs
                slate
                class="!text-sm !text-n-slate-11 flex-shrink-0"
                @click="addScenario(item)"
              />
            </div>
            <div class="flex flex-col">
              <span class="text-sm text-n-slate-11 mt-2">
                {{ item.description }}
              </span>
              <component
                :is="renderInstruction(formatMessage(item.instruction, false))"
              />
              <span class="text-sm text-n-slate-11 font-medium mb-1">
                {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TOOLS_USED') }}
                {{ item.tools?.map(tool => `@${tool}`).join(', ') }}
              </span>
            </div>
          </template>
        </SuggestedScenarios>
      </div>
      <div class="flex mt-7 flex-col gap-4">
        <div class="flex justify-between items-center">
          <BulkSelectBar
            v-model="bulkSelectedIds"
            :all-items="scenarios"
            :select-all-label="buildSelectedCountLabel"
            :selected-count-label="selectedCountLabel"
            :delete-label="
              $t('CAPTAIN.ASSISTANTS.SCENARIOS.BULK_ACTION.BULK_DELETE_BUTTON')
            "
            @bulk-delete="bulkDeleteScenarios"
          >
            <template #default-actions>
              <AddNewScenariosDialog @add="addScenario" />
            </template>
          </BulkSelectBar>
          <div
            v-if="scenarios.length && bulkSelectedIds.size === 0"
            class="max-w-[22.5rem] w-full min-w-0"
          >
            <Input
              v-model="searchQuery"
              :placeholder="
                t('CAPTAIN.ASSISTANTS.SCENARIOS.LIST.SEARCH_PLACEHOLDER')
              "
            />
          </div>
        </div>
        <div v-if="scenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else-if="filteredScenarios.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ t('CAPTAIN.ASSISTANTS.SCENARIOS.SEARCH_EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else class="flex flex-col gap-2">
          <ScenariosCard
            v-for="scenario in filteredScenarios"
            :id="scenario.id"
            :key="scenario.id"
            :title="scenario.title"
            :description="scenario.description"
            :instruction="scenario.instruction"
            :tools="scenario.tools"
            :is-selected="bulkSelectedIds.has(scenario.id)"
            :selectable="
              hoveredCard === scenario.id || bulkSelectedIds.size > 0
            "
            @select="handleRuleSelect"
            @delete="deleteScenario(scenario.id)"
            @update="updateScenario"
            @hover="isHovered => handleRuleHover(isHovered, scenario.id)"
          />
        </div>
      </div>

      <SyncPresetDialog
        v-if="businessPresetId"
        ref="syncPresetDialogRef"
        :preset-id="businessPresetId"
        :assistant-id="assistantId"
        @synced="refreshScenarios"
      />

      <LinkPresetDialog
        v-else
        ref="linkPresetDialogRef"
        :assistant-id="assistantId"
        @linked="handleLinked"
      />

      <GenerateScenariosDialog
        ref="generateDialogRef"
        :assistant-id="assistantId"
        @generated="startAnalysis"
      />
    </template>
  </PageLayout>
</template>
